import React, { useState, useEffect } from "react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Target, CalendarClock, Loader2, Pencil, Trophy, Share2, PartyPopper } from "lucide-react";

const fireConfetti = () => {
  const end = Date.now() + 900;
  const colors = ["#00F2FE", "#FE2C55", "#ffffff"];
  (function frame() {
    confetti({ particleCount: 4, angle: 60, spread: 65, origin: { x: 0 }, colors });
    confetti({ particleCount: 4, angle: 120, spread: 65, origin: { x: 1 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
};

const fmt = (n) => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return String(n ?? 0);
};

const Ring = ({ progress }) => {
  const r = 54;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - Math.min(100, progress || 0) / 100);
  return (
    <svg width="130" height="130" viewBox="0 0 130 130" className="shrink-0">
      <circle cx="65" cy="65" r={r} fill="none" stroke="hsl(240 3.7% 15.9%)" strokeWidth="10" />
      <circle cx="65" cy="65" r={r} fill="none" stroke="hsl(191 100% 50%)" strokeWidth="10" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={offset} transform="rotate(-90 65 65)"
        style={{ transition: "stroke-dashoffset 0.8s ease" }} />
      <text x="65" y="61" textAnchor="middle" className="fill-foreground font-heading" fontSize="28" fontWeight="900">{Math.round(progress || 0)}%</text>
      <text x="65" y="82" textAnchor="middle" className="fill-muted-foreground" fontSize="11">to goal</text>
    </svg>
  );
};

const GoalCard = ({ goal, platform, platformName, onUpdated }) => {
  const [open, setOpen] = useState(false);
  const [showBadge, setShowBadge] = useState(false);
  const [target, setTarget] = useState(goal?.target ? String(goal.target) : "");
  const [saving, setSaving] = useState(false);

  const reached = goal && goal.remaining <= 0;

  useEffect(() => { setTarget(goal?.target ? String(goal.target) : ""); }, [goal?.target, platform]);

  useEffect(() => {
    if (reached) {
      const key = `cr_celebrated_${platform}_${goal.target}`;
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, "1");
        fireConfetti();
        setShowBadge(true);
      }
    }
  }, [reached, platform, goal?.target]);

  const shareBadge = async () => {
    const msg = `🎉 I just hit ${fmt(goal.target)} followers on ${platformName} with CloutRise!`;
    const url = window.location.origin;
    try {
      if (navigator.share) await navigator.share({ title: "CloutRise Goal Smashed", text: msg, url });
      else { await navigator.clipboard.writeText(`${msg} ${url}`); toast.success("Badge copied — paste it anywhere!"); }
    } catch { /* user dismissed share sheet */ }
  };

  const save = async () => {
    const t = parseInt(target || "0", 10);
    if (!t || t <= 0) { toast.error("Enter a target follower count"); return; }
    setSaving(true);
    try {
      await api.post("/goal", { platform, target: t });
      toast.success("Goal set!");
      setOpen(false);
      onUpdated && onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail ? String(e.response.data.detail) : "Could not save goal"); }
    finally { setSaving(false); }
  };

  const etaDate = goal?.eta_days ? new Date(Date.now() + goal.eta_days * 86400000).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null;

  return (
    <div className="rounded-xl border border-border/50 bg-card p-6" data-testid="goal-card">
      {!goal ? (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Target className="w-5 h-5" /></div>
            <div>
              <div className="font-heading text-lg font-semibold">Set a follower goal</div>
              <div className="text-sm text-muted-foreground">Track progress and see your ETA on {platformName || "this platform"}.</div>
            </div>
          </div>
          <Button onClick={() => setOpen(true)} className="rounded-full bg-primary text-background font-bold" data-testid="set-goal-btn">Set goal</Button>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <Ring progress={goal.progress} />
          <div className="flex-1 w-full">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><Target className="w-4 h-4 text-primary" /> {platformName} goal</h3>
              <Button variant="ghost" size="sm" onClick={() => setOpen(true)} className="text-muted-foreground hover:text-foreground" data-testid="edit-goal-btn"><Pencil className="w-4 h-4" /></Button>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><div className="text-xs text-muted-foreground">Current</div><div className="font-heading text-xl font-black">{fmt(goal.current)}</div></div>
              <div><div className="text-xs text-muted-foreground">Target</div><div className="font-heading text-xl font-black">{fmt(goal.target)}</div></div>
              <div><div className="text-xs text-muted-foreground">Remaining</div><div className="font-heading text-xl font-black text-secondary">{fmt(goal.remaining)}</div></div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              {reached ? (
                <>
                  <span className="text-primary font-semibold flex items-center gap-1.5"><PartyPopper className="w-4 h-4" /> Goal smashed — {fmt(goal.target)} reached!</span>
                  <Button size="sm" onClick={() => setShowBadge(true)} className="rounded-full bg-primary text-background font-bold h-8" data-testid="share-badge-btn">
                    <Share2 className="w-3.5 h-3.5 mr-1" /> Share badge
                  </Button>
                </>
              ) : (
                <>
                  <CalendarClock className="w-4 h-4 text-primary" />
                  {goal.eta_days > 0 ? <span>On track to hit <b className="text-foreground">{fmt(goal.target)}</b> in <b className="text-primary">~{goal.eta_days} days</b>{etaDate ? ` (≈ ${etaDate})` : ""}</span>
                    : <span>Keep your campaign active to project an ETA.</span>}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle className="font-heading">{platformName} follower goal</DialogTitle>
            <DialogDescription>Set the follower count you want to reach on {platformName || "this platform"}.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-sm">Target follower count</Label>
            <Input type="number" min="1" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="e.g. 50000"
              className="mt-1.5 h-11 bg-transparent border-white/10 focus:border-primary" data-testid="goal-target-input" />
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} className="rounded-full bg-primary text-background font-bold" data-testid="goal-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save goal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showBadge} onOpenChange={setShowBadge}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Goal smashed! 🎉</DialogTitle>
            <DialogDescription>Share your milestone badge with your audience.</DialogDescription>
          </DialogHeader>
          <div className="relative overflow-hidden rounded-2xl border border-primary/40 p-8 text-center" data-testid="goal-badge"
            style={{ background: "radial-gradient(ellipse at top, rgba(0,242,254,0.18), transparent 70%), #0b0b0d" }}>
            <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-56 h-56 rounded-full bg-primary/20 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="w-16 h-16 rounded-2xl bg-primary/15 border border-primary/30 flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-8 h-8 text-primary" />
              </div>
              <div className="text-xs uppercase tracking-[0.25em] text-primary font-bold">Goal Smashed</div>
              <div className="font-heading text-4xl font-black mt-2">{fmt(goal?.target)}</div>
              <div className="text-sm text-muted-foreground mt-1">followers on {platformName}</div>
              <div className="mt-5 pt-4 border-t border-white/10 text-sm font-semibold flex items-center justify-center gap-1.5">
                <span className="text-primary">Clout</span><span>Rise</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={shareBadge} className="w-full rounded-full bg-primary text-background font-bold" data-testid="badge-share-btn">
              <Share2 className="w-4 h-4 mr-2" /> Share my badge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GoalCard;
